/**
 * floating.ts — pure positioning math for anchored floating panels (tooltips, popovers,
 * menus, the date-picker calendar). PO ruling 2026-10-05 ("border aware tooltips also"):
 * a floating panel must never be clipped by an ancestor's `overflow: hidden` (every
 * `.argus-card` has it, and `InfoTip` sits inside cards) and must never run past the
 * viewport edge — it flips to the opposite side when the requested side has no room, and is
 * clamped along the cross axis (and, as a last resort in a very small viewport, along the
 * main axis too) to stay fully on screen with a `margin` gutter.
 *
 * This file has no DOM dependency and no positioning library: `useFloating`
 * (./use-floating.ts) is the only caller, from a panel portaled into `document.body` with
 * `position: fixed`, so the coordinates this returns are VIEWPORT coordinates — once the
 * panel leaves the clipped ancestor's stacking context, that ancestor's `overflow: hidden`
 * and `scroll` offset no longer clip or move it.
 */

export type FloatingSide = 'top' | 'bottom' | 'left' | 'right';
export type FloatingAlign = 'start' | 'end' | 'center';

export type FloatingPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'bottom'
  | 'top-start'
  | 'top-end'
  | 'top'
  | 'right'
  | 'left';

/** The anchor's rect. A live `getBoundingClientRect()` result fits this shape directly;
 *  tests pass a plain object. */
export interface FloatingAnchorRect {
  readonly top: number;
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
  readonly width: number;
  readonly height: number;
}

export interface FloatingPanelSize {
  readonly width: number;
  readonly height: number;
}

export interface FloatingViewportSize {
  readonly width: number;
  readonly height: number;
}

export interface PositionFloatingOptions {
  anchor: FloatingAnchorRect;
  panel: FloatingPanelSize;
  placement: FloatingPlacement;
  /** Gap kept between the anchor and the panel along the main axis. Default 6. */
  offset?: number | undefined;
  /** Minimum gutter kept between the panel and every viewport edge. Default 8. */
  margin?: number | undefined;
  viewport: FloatingViewportSize;
}

export interface FloatingPosition {
  /** Viewport-relative (`position: fixed`) top, in pixels. */
  top: number;
  /** Viewport-relative (`position: fixed`) left, in pixels. */
  left: number;
  /** The side/align actually used, after any flip — never just the request echoed back. */
  placement: FloatingPlacement;
}

function splitPlacement(placement: FloatingPlacement): {
  side: FloatingSide;
  align: FloatingAlign;
} {
  switch (placement) {
    case 'bottom':
      return { side: 'bottom', align: 'center' };
    case 'bottom-start':
      return { side: 'bottom', align: 'start' };
    case 'bottom-end':
      return { side: 'bottom', align: 'end' };
    case 'top':
      return { side: 'top', align: 'center' };
    case 'top-start':
      return { side: 'top', align: 'start' };
    case 'top-end':
      return { side: 'top', align: 'end' };
    case 'right':
      return { side: 'right', align: 'center' };
    case 'left':
      return { side: 'left', align: 'center' };
  }
}

function joinPlacement(side: FloatingSide, align: FloatingAlign): FloatingPlacement {
  return align === 'center'
    ? (side as FloatingPlacement)
    : (`${side}-${align}` as FloatingPlacement);
}

/** Clamps `value` into `[min, max]`. When the panel is bigger than the room available
 *  (`max < min`) there is no in-bounds position — pin to `min` (the near edge) rather than
 *  produce a backwards range. */
function clamp(value: number, min: number, max: number): number {
  return max < min ? min : Math.min(Math.max(value, min), max);
}

function resolveVerticalSide(
  requested: 'top' | 'bottom',
  anchor: FloatingAnchorRect,
  panelHeight: number,
  offset: number,
  viewport: FloatingViewportSize,
): 'top' | 'bottom' {
  const spaceBelow = viewport.height - anchor.bottom;
  const spaceAbove = anchor.top;
  const fitsBelow = spaceBelow - offset >= panelHeight;
  const fitsAbove = spaceAbove - offset >= panelHeight;
  if (requested === 'bottom') {
    if (fitsBelow) return 'bottom';
    if (fitsAbove) return 'top';
    return spaceBelow >= spaceAbove ? 'bottom' : 'top';
  }
  if (fitsAbove) return 'top';
  if (fitsBelow) return 'bottom';
  return spaceAbove >= spaceBelow ? 'top' : 'bottom';
}

function resolveHorizontalSide(
  requested: 'left' | 'right',
  anchor: FloatingAnchorRect,
  panelWidth: number,
  offset: number,
  viewport: FloatingViewportSize,
): 'left' | 'right' {
  const spaceRight = viewport.width - anchor.right;
  const spaceLeft = anchor.left;
  const fitsRight = spaceRight - offset >= panelWidth;
  const fitsLeft = spaceLeft - offset >= panelWidth;
  if (requested === 'right') {
    if (fitsRight) return 'right';
    if (fitsLeft) return 'left';
    return spaceRight >= spaceLeft ? 'right' : 'left';
  }
  if (fitsLeft) return 'left';
  if (fitsRight) return 'right';
  return spaceLeft >= spaceRight ? 'left' : 'right';
}

function crossAxisHorizontal(
  anchor: FloatingAnchorRect,
  panelWidth: number,
  align: FloatingAlign,
): number {
  if (align === 'start') return anchor.left;
  if (align === 'end') return anchor.right - panelWidth;
  return anchor.left + anchor.width / 2 - panelWidth / 2;
}

function crossAxisVertical(anchor: FloatingAnchorRect, panelHeight: number): number {
  return anchor.top + anchor.height / 2 - panelHeight / 2;
}

export function positionFloating({
  anchor,
  panel,
  placement,
  offset = 6,
  margin = 8,
  viewport,
}: PositionFloatingOptions): FloatingPosition {
  const { side: requestedSide, align } = splitPlacement(placement);
  const vertical = requestedSide === 'top' || requestedSide === 'bottom';

  const side = vertical
    ? resolveVerticalSide(requestedSide, anchor, panel.height, offset, viewport)
    : resolveHorizontalSide(requestedSide, anchor, panel.width, offset, viewport);

  let top: number;
  let left: number;
  if (side === 'bottom') {
    top = anchor.bottom + offset;
    left = crossAxisHorizontal(anchor, panel.width, align);
  } else if (side === 'top') {
    top = anchor.top - offset - panel.height;
    left = crossAxisHorizontal(anchor, panel.width, align);
  } else if (side === 'right') {
    left = anchor.right + offset;
    top = crossAxisVertical(anchor, panel.height);
  } else {
    left = anchor.left - offset - panel.width;
    top = crossAxisVertical(anchor, panel.height);
  }

  return {
    top: clamp(top, margin, viewport.height - panel.height - margin),
    left: clamp(left, margin, viewport.width - panel.width - margin),
    placement: joinPlacement(side, align),
  };
}
